import {
	Building2,
	CalendarClock,
	FileArchive,
	History,
	Image,
	Map as MapIcon,
	LayoutDashboard,
	ListVideo,
	MonitorSmartphone,
	Settings,
	UserCog,
	Users,
	type LucideIcon,
} from 'lucide-react';
import { matchPath } from 'react-router-dom';
import { ROUTES } from '@/constants';
import type { AuthRole } from '@/features/auth';

export interface NavigationItem {
	label: string;
	to: string;
	icon: LucideIcon;
	/** Match only the exact path (used for the index route). */
	end?: boolean;
	/** When set, the item is only shown to these roles. */
	roles?: AuthRole[];
	/** Also in the mobile tab bar (the mobile CMS has only these sections). */
	mobile?: boolean;
}

/**
 * Primary navigation. Add an entry here to surface a route in the sidebar.
 */
export const navigationItems: NavigationItem[] = [
	{ label: 'Dashboard', to: ROUTES.home, icon: LayoutDashboard, end: true },
	{
		label: 'Displays',
		to: ROUTES.displays,
		icon: MonitorSmartphone,
		mobile: true,
	},
	{ label: 'Buildings', to: ROUTES.buildings, icon: MapIcon, mobile: true },
	{ label: 'Media', to: ROUTES.media, icon: Image, mobile: true },
	{ label: 'Customers', to: ROUTES.customers, icon: Users },
	{ label: 'Playlists', to: ROUTES.playlists, icon: ListVideo },
	{ label: 'Schedules', to: ROUTES.schedules, icon: CalendarClock },
	{
		label: 'Audit',
		to: ROUTES.audit,
		icon: History,
		roles: ['platform_admin', 'admin'],
	},
	{
		label: 'Reports',
		to: ROUTES.reports,
		icon: FileArchive,
		roles: ['platform_admin', 'admin'],
	},
	{
		label: 'Users',
		to: ROUTES.users,
		icon: UserCog,
		roles: ['platform_admin', 'admin'],
	},
	{
		label: 'Tenants',
		to: ROUTES.tenants,
		icon: Building2,
		roles: ['platform_admin'],
	},
	{ label: 'Settings', to: ROUTES.settings, icon: Settings },
];

/** Sections of the mobile tab bar, in sidebar order. */
export const mobileNavigationItems = navigationItems.filter(
	(item) => item.mobile,
);

/**
 * Pages usable on a phone. Editors that need a large screen (power plans,
 * layouts) stay desktop only even though their section is in the tab bar.
 */
const MOBILE_ROUTE_PATTERNS = [
	ROUTES.displays,
	ROUTES.buildings,
	'/buildings/:buildingId',
	'/buildings/:buildingId/floors/:floorId',
	ROUTES.media,
];

/** Whether the mobile CMS shows `pathname` (otherwise: "use a computer"). */
export function isMobileRoute(pathname: string): boolean {
	return MOBILE_ROUTE_PATTERNS.some(
		(pattern) => matchPath({ path: pattern, end: true }, pathname) !== null,
	);
}
